var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { Button, useMediaQuery, useTheme } from "@mui/material";
import { loadStripe } from "@stripe/stripe-js";
import { useContext, useEffect, useState } from "react";
import { useRouteMatch } from "../../../../../Shared/hooks/useRouteMatch";
import useToast from "../../../../../hooks/useToast";
import { AcceptHostedForm } from "../../../Authorize/AcceptHostedForm";
import { BillingInformationContext } from "../../../BillingInformationContext";
import { GetStripeAppearance } from "../../../Stripe/StripeCardForm-utils";
import { usePaymentMethodCreate } from "../PaymentMethodCreate/usePaymentMethodCreate";
import { usePaymentMethodFetch } from "../PaymentMethodFetch/usePaymentMethodFetch";
import { usePaymentMethodUpdate } from "../PaymentMethodUpdate/usePaymentMethodUpdate";
/**
 * Custom hook for billing information
 * Created a hook to encapsulate the billing information logic so that it can be used in other components.
 * This allows us to update the billing context reliably and consistently across the app.
 *
 * @returns This returns the billing information state and functions to update the state.
 * @note This hook can be called regardless as to whether or not the returned values are used.
 * @note See useAddresses.tsx for an example.
 */
export const useBillingInformation = () => {
    const [isStripeCardFormOpen, setIsStripeCardFormOpen] = useState(false);
    const [isTapCardFormOpen, setIsTapCardFormOpen] = useState(false);
    const [isGiftCardModalOpen, setIsGiftCardModalOpen] = useState(false);
    const [isOldGiftCardsModalOpen, setIsOldGiftCardsModalOpen] = useState(false);
    const [isGiftCardMoreInfoModalOpen, setIsGiftCardMoreInfoModalOpen] = useState(false);
    const [isRemoveCardModalOpen, setIsRemoveCardModalOpen] = useState(false);
    const [selectedCard, setSelectedCard] = useState();
    const [selectedGiftCard, setSelectedGiftCard] = useState();
    const { loading: paymentMethodsLoading, getPaymentMethods } = usePaymentMethodFetch();
    const [stripePromise, setStripePromise] = useState(null);
    const [usingStripe, setUsingStripe] = useState(false);
    const toast = useToast();
    const { setClientSecret, clientSecret, token, publicKey, customerCreditCards, customerGiftCards, oldGiftCards, stripePublishableKey: stripePK, authorizeUrl, enableGiftCards, setCustomerGiftCards, setOldGiftCards, } = useContext(BillingInformationContext);
    const { initPaymentMethodCreation, loading } = usePaymentMethodCreate();
    const { initPaymentMethodUpdate, updatePrimaryPaymentMethod, deactivatePaymentMethod } = usePaymentMethodUpdate();
    const { isOrderFlowPage } = useRouteMatch();
    useEffect(() => {
        if (stripePK && clientSecret && !stripePromise) {
            setStripePromise(loadStripe(stripePK));
            setUsingStripe(true);
        }
    }, [stripePK, stripePromise, clientSecret]);
    useEffect(() => {
        if (!isOrderFlowPage) {
            initPaymentMethodCreation();
            getPaymentMethods();
        }
    }, [isOrderFlowPage]);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
    const authAddButton = () => {
        return (_jsx(AcceptHostedForm, Object.assign({ formToken: token === null || token === void 0 ? void 0 : token.toString(), url: authorizeUrl, showPrimaryCheckbox: false }, { children: _jsx(Button, Object.assign({ variant: "contained", size: "small", color: "primary", type: "submit", fullWidth: true, disabled: loading }, { children: "Add Payment Method" })) })));
    };
    const paymentMethodUpdate = (paymentMethodId) => {
        initPaymentMethodUpdate(paymentMethodId)
            .then((res) => {
            if (!res.success) {
                toast.push("Something unexpected happened. Please try again", { variant: "error" });
            }
            else {
                if (publicKey) {
                    setIsTapCardFormOpen(true);
                }
                if (clientSecret) {
                    setIsStripeCardFormOpen(true);
                }
            }
        })
            .catch(() => toast.push("Something unexpected happened. Please try again", { variant: "error" }));
    };
    const appearance = GetStripeAppearance();
    const options = {
        clientSecret: clientSecret,
        appearance,
    };
    const primaryPaymentMethodToggle = (paymentMethodId) => __awaiter(void 0, void 0, void 0, function* () {
        if (paymentMethodId) {
            yield updatePrimaryPaymentMethod(paymentMethodId);
        }
    });
    const removePaymentMethod = (paymentMethodId) => __awaiter(void 0, void 0, void 0, function* () {
        if (paymentMethodId) {
            yield deactivatePaymentMethod(paymentMethodId);
        }
    });
    return {
        isStripeCardFormOpen,
        isTapCardFormOpen,
        isGiftCardModalOpen,
        isOldGiftCardsModalOpen,
        isGiftCardMoreInfoModalOpen,
        isRemoveCardModalOpen,
        selectedCard,
        selectedGiftCard,
        paymentMethodsLoading,
        usingStripe,
        stripePromise,
        setStripePromise,
        setIsStripeCardFormOpen,
        setIsTapCardFormOpen,
        setIsGiftCardModalOpen,
        setIsOldGiftCardsModalOpen,
        setIsGiftCardMoreInfoModalOpen,
        setIsRemoveCardModalOpen,
        setSelectedCard,
        setSelectedGiftCard,
        authAddButton,
        paymentMethodUpdate,
        appearance,
        options,
        publicKey,
        loading,
        isMobile,
        customerCreditCards,
        customerGiftCards,
        oldGiftCards,
        token,
        authorizeUrl,
        clientSecret,
        setClientSecret,
        initPaymentMethodCreation,
        stripePK,
        primaryPaymentMethodToggle,
        removePaymentMethod,
        enableGiftCards,
        setCustomerGiftCards,
        setOldGiftCards,
    };
};
